import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';

import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  BioentityLinkFieldWithTagSchema,
  isRequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import FormGroup from 'ui-library/bs/FormGroup';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

import CreateComponentModal from './CreateComponentModal';

const SELECT_NO_FIELD_DUMMY_OBJECT = {
  displayName: 'None',
};

// This is not an available option in the dropdown
const UNSELECTED_DUMMY_OBJECT = {
  displayName: 'Select an option',
};

export interface ConfirmCreateProteinPayload {
  name: string;
  folderId: string;
  translationLink?: BioentityLinkFieldWithTagSchema | null;
  tagSchemaId?: string;
  studyIds?: Array<string>;
}

type TranslationLinkFields = Array<{
  field: BioentityLinkFieldWithTagSchema;
  isSingleLinkFieldWithExistingValue: boolean;
}>;

export interface CreateProteinModalProps {
  initialFolderId?: string;
  namePlaceholder?: string;
  onRequestSubmit: (result: ConfirmCreateProteinPayload) => void;
  onRequestClose: () => void;
  translationLinkFields?: TranslationLinkFields;
}

type TranslationLinkOrDummy =
  | SchemaField
  | typeof UNSELECTED_DUMMY_OBJECT
  | typeof SELECT_NO_FIELD_DUMMY_OBJECT;

function isRealTranslationLink(
  translationLinkOrDummy: TranslationLinkOrDummy
): translationLinkOrDummy is BioentityLinkFieldWithTagSchema {
  return ![UNSELECTED_DUMMY_OBJECT, SELECT_NO_FIELD_DUMMY_OBJECT].includes(translationLinkOrDummy);
}

function CreateProteinModal({
  initialFolderId,
  namePlaceholder,
  onRequestSubmit,
  onRequestClose,
  translationLinkFields = [],
}: CreateProteinModalProps): JSX.Element {
  const [tagSchemaId, setTagSchemaId] = useState<string | null>(null);
  const [translationLink, setTranslationLink] = useState<TranslationLinkOrDummy>(UNSELECTED_DUMMY_OBJECT);
  const [selectedStudyItems, setSelectedStudyItems] = useState<Array<StudySearchItem>>([]);

  const hasCompatibleTranslationLinkOptions = (): boolean =>
    translationLinkFields.some(
      (translationLinkField) =>
        tagSchemaId == null ||
        // @ts-expect-error Assumes no batch schemas
        translationLinkField.field.requiredLink?.tagSchema.id === tagSchemaId
    );

  const getTranslationLinkOptions = (): Array<{
    key: number | string;
    name: string;
    handleOnClick: () => void;
    disabled?: boolean;
  }> => [
    {
      key: 'None',
      name: 'None',
      handleOnClick: () => {
        handleChangeTranslationLink(SELECT_NO_FIELD_DUMMY_OBJECT);
      },
    },
    ...translationLinkFields
      .filter(
        (translationLinkField) =>
          tagSchemaId == null ||
          // @ts-expect-error Assumes no batch schemas
          translationLinkField.field.requiredLink?.tagSchema.id === tagSchemaId
      )
      .map(({field, isSingleLinkFieldWithExistingValue}) => {
        if (isSingleLinkFieldWithExistingValue) {
          return {
            key: field.id,
            disabled: true,
            name: `${field.displayName} (Already specified))`,
            handleOnClick: noop,
          };
        } else {
          return {
            key: field.id,
            name: field.displayName,
            handleOnClick: () => {
              handleChangeTranslationLink(field);
            },
          };
        }
      }),
  ];

  const handleChangeTranslationLink = (field: TranslationLinkOrDummy): void => {
    setTranslationLink(field);
    if (isRealTranslationLink(field)) {
      // @ts-expect-error Assumes no batch schemas
      setTagSchemaId(field.requiredLink?.tagSchema.id);
    }
  };

  const handleConfirm = ({folderId, name}: {folderId: string; name: string}): void => {
    const translationLinkValue: BioentityLinkFieldWithTagSchema | null = !isRealTranslationLink(
      translationLink
    )
      ? null
      : translationLink;
    const result: ConfirmCreateProteinPayload = {folderId, name, translationLink: translationLinkValue};
    if (translationLinkValue == null && tagSchemaId != null) {
      result.tagSchemaId = tagSchemaId;
    }
    if (selectedStudyItems.length > 0) {
      result.studyIds = selectedStudyItems.map((studyItem) => studyItem.id);
    }
    onRequestSubmit(result);
  };

  return (
    <CreateComponentModal
      additionalSubmitDisabled={
        hasCompatibleTranslationLinkOptions() && translationLink === UNSELECTED_DUMMY_OBJECT
      }
      confirmButtonText="Create"
      initialFolderId={initialFolderId}
      readableItemType="entity"
      namePlaceholder={namePlaceholder}
      onConfirm={handleConfirm}
      onRequestClose={onRequestClose}
      title="Create AA sequence"
    >
      {translationLinkFields.length > 0 && (
        <FormGroup>
          <FieldLabel label="Translation link" />
          <SelectDropdown
            disabled={!hasCompatibleTranslationLinkOptions()}
            displayName={
              hasCompatibleTranslationLinkOptions()
                ? translationLink.displayName
                : 'No compatible fields available'
            }
            menu={
              <Menu width={MenuWidth.FULL}>
                {getTranslationLinkOptions().map(({key, name, handleOnClick, disabled}) => (
                  <Option key={key} isDisabled={disabled} onSelect={handleOnClick}>
                    {name}
                  </Option>
                ))}
              </Menu>
            }
          />
        </FormGroup>
      )}
      {isFeatureEnabled({feature: RegistryFeature.REGISTRY_SET_SCHEMA}) && (
        <FormGroup>
          <FieldLabel label="Schema" />
          <TagSchemaSelector
            allowNone={true}
            isDisabled={
              isRealTranslationLink(translationLink) ||
              // @ts-expect-error This is a SchemaField, but we're passing it to this fn for some reason
              isRequiredLinkTagSchema(translationLink)
            }
            oligoAwareFolderItemTypes={[ModelTypeConstants.PROTEIN]}
            selectedTagSchemaId={tagSchemaId}
            tagSchemaFilterFn={(schema) =>
              // @ts-expect-error This is a SchemaField, but we're passing it to this fn for some reason
              !isRequiredLinkTagSchema(translationLink) ||
              // @ts-expect-error isRequiredLinkTagSchema should always fail :|
              translationLink.requiredLink.tagSchema.id === schema.id
            }
            onChange={(tagSchema) => {
              setTagSchemaId(tagSchema ? tagSchema.id : null);
            }}
          />
        </FormGroup>
      )}
      {BenchlingConfig.STUDIES_ENABLE && (
        <FormGroup>
          <FieldLabel label="Studies" />
          <StudySearchInput
            selectedItems={selectedStudyItems}
            shouldAllowMultiple={true}
            onChange={(studySearchItems) => {
              setSelectedStudyItems(studySearchItems);
            }}
          />
        </FormGroup>
      )}
    </CreateComponentModal>
  );
}

export default CreateProteinModal;
